/**
 * Срез MIDI: выделение событий одного канала в диапазоне `[startTick, endTick)`
 * с правилами clamp для длинных нот и фильтрацией CC / pitch bend по
 * whitelist'у Fantom-совместимого MIDI.
 *
 * Возвращаемая `Midi` всегда имеет `format: 1` с header-треком (tempos,
 * time-signatures, key-signatures) и одним sliced-треком на канале 0.
 * Готов к `encodeMidi`.
 *
 * Правила:
 * - Ноты, начатые до `startTick` и заканчивающиеся после `endTick`,
 *   обрезаются в `[startTick, endTick)`.
 * - Ноты вне `[startTick, endTick)` (полностью) дропаются.
 * - PC: первая PC в срезе; если нет — последняя PC до среза из исходного
 *   MIDI. В срез попадает ровно одна PC, и она переносится в тик 0
 *   относительно среза.
 * - CC: только `ALLOWED_CONTROL_CHANGES`. Pitch Bend — отбрасывается.
 * - Ноты сортируются по тикам.
 */

import {
  DRUM_CHANNEL,
  type Midi,
  type MidiChannel,
  type MidiControlChange,
  type MidiHeader,
  type MidiInstrument,
  type MidiKeySignature,
  type MidiMetaEvent,
  type MidiNote,
  type MidiProgramChange,
  type MidiSysEx,
  type MidiTempo,
  type MidiTimeSignature,
  type MidiTrack,
  type MidiUnknownMeta,
} from "@midi-fantom-utils/midi-core";
import { ALLOWED_CONTROL_CHANGES } from "../transform/shared.js";
import { recomputeDurationSeconds } from "../transform/duration.js";

const EMPTY_SYSEX: ReadonlyArray<MidiSysEx> = [];
const EMPTY_PB: ReadonlyArray<unknown> = [];
const EMPTY_META: ReadonlyArray<MidiMetaEvent> = [];
const EMPTY_UNK_META: ReadonlyArray<MidiUnknownMeta> = [];

const DEFAULT_INSTRUMENT = (channel: MidiChannel): MidiInstrument =>
  channel === DRUM_CHANNEL
    ? {
        family: "percussive",
        number: 0,
        name: "standard kit",
        percussion: true,
      }
    : {
        family: "piano",
        number: 0,
        name: "acoustic grand piano",
        percussion: false,
      };

const collectNotesForChannel = (
  midi: Midi,
  channel: MidiChannel
): MidiNote[] => {
  const out: MidiNote[] = [];
  for (const track of midi.tracks) {
    for (const n of track.notes) {
      const ch: MidiChannel = n.channel ?? track.channel;
      if (ch !== channel) continue;
      out.push(n);
    }
  }
  return out;
};

const collectCcForChannel = (
  midi: Midi,
  channel: MidiChannel
): MidiControlChange[] => {
  const out: MidiControlChange[] = [];
  for (const track of midi.tracks) {
      const allowed = ALLOWED_CONTROL_CHANGES as readonly number[];
      for (const [number, list] of track.controlChanges) {
        if (!allowed.includes(number)) continue;
      for (const cc of list) {
        const ch: MidiChannel = cc.channel ?? track.channel;
        if (ch !== channel) continue;
        out.push(cc);
      }
    }
  }
  return out;
};

const collectPcForChannel = (
  midi: Midi,
  channel: MidiChannel
): MidiProgramChange[] => {
  const out: MidiProgramChange[] = [];
  for (const track of midi.tracks) {
    for (const pc of track.programChanges) {
      const ch: MidiChannel = pc.channel ?? track.channel;
      if (ch !== channel) continue;
      out.push(pc);
    }
  }
  return out.sort((a, b) => a.ticks - b.ticks);
};

export type SliceResult = {
  noteCount: number;
  lengthTicks: number;
  midi: Midi;
  sourceChannel: MidiChannel;
};

export const sliceMidi = (
  source: Midi,
  startTick: number,
  endTick: number,
  channel: MidiChannel
): SliceResult | null => {
  if (!Number.isFinite(startTick) || !Number.isFinite(endTick)) return null;
  if (endTick <= startTick) return null;

  const allNotes = collectNotesForChannel(source, channel);
  const allCc = collectCcForChannel(source, channel);
  const allPc = collectPcForChannel(source, channel);

  const slicedNotes: MidiNote[] = [];
  for (const n of allNotes) {
    const noteStart = n.ticks;
    const noteEnd = n.ticks + n.durationTicks;
    if (noteEnd <= startTick) continue;
    if (noteStart >= endTick) continue;
    const clampedStart = Math.max(noteStart, startTick);
    const clampedEnd = Math.min(noteEnd, endTick);
    if (clampedEnd <= clampedStart) continue;
    slicedNotes.push({
      ...n,
      ticks: clampedStart - startTick,
      durationTicks: clampedEnd - clampedStart,
      // Preserve the source channel — drum slices (channel === 9) MUST
      // stay on channel 9 in the exported MID, otherwise Fantom PTN
      // IMPORT routes them to a melodic zone. Melodic channels get
      // re-stamped to 0 to match the single-track output.
      channel: channel === DRUM_CHANNEL ? DRUM_CHANNEL : 0,
    });
  }
  slicedNotes.sort((a, b) => a.ticks - b.ticks || a.midi - b.midi);

  const slicedCc: MidiControlChange[] = [];
  for (const cc of allCc) {
    if (cc.ticks < startTick || cc.ticks >= endTick) continue;
    slicedCc.push({
      ...cc,
      ticks: cc.ticks - startTick,
      channel: channel === DRUM_CHANNEL ? DRUM_CHANNEL : 0,
    });
  }
  slicedCc.sort((a, b) => a.ticks - b.ticks || a.number - b.number);

  const pcInRange = allPc.filter(
    (pc) => pc.ticks >= startTick && pc.ticks < endTick
  );
  const pcBefore = allPc.filter((pc) => pc.ticks <= startTick);
  const resolvedPc: MidiProgramChange | null =
    pcInRange.length > 0
      ? pcInRange[0]!
      : pcBefore.length > 0
        ? pcBefore[pcBefore.length - 1]!
        : null;

  const slicedPc: MidiProgramChange[] = [];
  if (resolvedPc) {
    slicedPc.push({
      ...resolvedPc,
      ticks: 0,
      channel: channel === DRUM_CHANNEL ? DRUM_CHANNEL : 0,
    });
  }

  const slicedTrack: MidiTrack = {
    name: "",
    // Трек-канал обязан совпадать с каналом нот для корректного импорта
    // в Fantom (drum → channel 9, melodic → channel 0).
    channel: channel === DRUM_CHANNEL ? DRUM_CHANNEL : 0,
    instrument: DEFAULT_INSTRUMENT(channel),
    notes: slicedNotes,
    controlChanges: groupCcByNumber(slicedCc),
    programChanges: slicedPc,
    pitchBends: EMPTY_PB as ReadonlyArray<MidiTrack["pitchBends"][number]>,
    sysex: EMPTY_SYSEX,
    meta: EMPTY_META,
    unknownMeta: EMPTY_UNK_META,
  };

  const header: MidiHeader = {
    name: source.header.name,
    ppq: source.header.ppq,
    tempos: shiftHeaderEvents(source.header.tempos, startTick, endTick),
    timeSignatures: shiftHeaderEvents(
      source.header.timeSignatures,
      startTick,
      endTick
    ),
    keySignatures: shiftHeaderEvents(
      source.header.keySignatures,
      startTick,
      endTick
    ),
    meta: EMPTY_META,
    durationSeconds: 0,
  };

  const midi: Midi = recomputeDurationSeconds({
    format: 1,
    header,
    tracks: [slicedTrack],
  });

  return {
    noteCount: slicedNotes.length,
    lengthTicks: endTick - startTick,
    midi,
    sourceChannel: channel,
  };
};

const groupCcByNumber = (
  ccs: MidiControlChange[]
): ReadonlyMap<number, ReadonlyArray<MidiControlChange>> => {
  const map = new Map<number, MidiControlChange[]>();
  for (const cc of ccs) {
    const list = map.get(cc.number) ?? [];
    list.push(cc);
    map.set(cc.number, list);
  }
  const out = new Map<number, ReadonlyArray<MidiControlChange>>();
  for (const [k, v] of map) out.set(k, v);
  return out;
};

type HeaderEventWithTicks = { ticks: number };

const shiftHeaderEvents = <T extends HeaderEventWithTicks>(
  events: ReadonlyArray<T>,
  startTick: number,
  endTick: number
): T[] => {
  const out: T[] = [];
  for (const ev of events) {
    if (ev.ticks < startTick || ev.ticks >= endTick) continue;
    out.push({ ...ev, ticks: ev.ticks - startTick });
  }
  out.sort((a, b) => a.ticks - b.ticks);
  return out;
};
